/**
 * @file src/local/localByteSources.js
 * @description Where the engine asks for a local entry's bytes.
 *
 * The audio layer must not know about the library — its handles, its permission state, its store.
 * It only needs one thing: given a `local:` track id, a supplier of bytes. This registry is that one
 * thing, and it is the only import the audio layer takes from `src/local/`. The library registers a
 * supplier per entry when it boots and when a file is added or re-attached; the supplier itself does
 * the work of opening the handle (or asking the desktop shell for the path) at play time, so a
 * permission that lapsed is discovered at the moment it matters and reported by the load, not
 * guessed at in advance.
 */
import { isLocalEntryId } from './localAudioIds.js';

const suppliers = new Map();

/**
 * @param {string} id a `local:` id
 * @param {() => Promise<Blob|Response>} supplier
 */
export function registerLocalByteSource(id, supplier) {
  if (!isLocalEntryId(id) || typeof supplier !== 'function') return;
  suppliers.set(id, supplier);
}

export function unregisterLocalByteSource(id) {
  suppliers.delete(id);
}

/** The supplier for `id`, or null when it is not a registered local entry. */
export function getLocalByteSource(id) {
  return isLocalEntryId(id) ? suppliers.get(id) ?? null : null;
}

export function hasLocalByteSource(id) {
  return isLocalEntryId(id) && suppliers.has(id);
}

export function resetLocalByteSourcesForTests() {
  suppliers.clear();
}
